import type { Content, TalentDef } from '@gra/content';
import { nextRandom } from './rng';
import { collectModifiers, computePlayerStats } from './stats';
import type { GameState } from './types';

export function xpForLevel(content: Content, level: number): number {
  return Math.round(content.game.xp.base * content.game.xp.growth ** (level - 1));
}

export function isTalentAvailable(s: GameState, t: TalentDef): boolean {
  if (t.classes.length > 0 && !t.classes.includes(s.classId)) return false;
  if ((s.talents[t.id] ?? 0) >= t.maxRank) return false;
  return t.requires.every((r) => (s.talents[r.talent] ?? 0) >= r.rank);
}

/** Prefiks wyboru, który podnosi rangę posiadanej umiejętności. */
export const UPGRADE_PREFIX = 'ulepszenie:';

/** Prefiks wyboru, który zamienia umiejętność na najwyższej randze w jej ewolucję. */
export const EVOLVE_PREFIX = 'ewolucja:';

/** Id umiejętności z wyboru-ulepszenia albo null, gdy to zwykły talent. */
export function upgradeTarget(choiceId: string): string | null {
  return choiceId.startsWith(UPGRADE_PREFIX) ? choiceId.slice(UPGRADE_PREFIX.length) : null;
}

/** Id umiejętności, która ma ewoluować, albo null. */
export function evolutionTarget(choiceId: string): string | null {
  return choiceId.startsWith(EVOLVE_PREFIX) ? choiceId.slice(EVOLVE_PREFIX.length) : null;
}

/**
 * Wybory po awansie. Co najmniej jeden dotyczy umiejętności (nowa umiejętność albo ulepszenie posiadanej),
 * reszta to dowolne talenty i ulepszenia.
 */
export function rollTalentChoices(s: GameState): string[] {
  const talents = Object.values(s.content.talents).filter((t) => isTalentAvailable(s, t));
  const upgrades = s.abilities.filter((a) => a.rank < s.content.game.abilityMaxRank).map((a) => UPGRADE_PREFIX + a.id);
  const skillPool = [...talents.filter((t) => t.grantsAbility).map((t) => t.id), ...upgrades];
  const pool = [...talents.map((t) => t.id), ...upgrades];
  // Ewolucja gotowa (umiejętność na najwyższej randze) zawsze trafia do wyboru.
  const out: string[] = s.abilities
    .filter((a) => a.rank >= s.content.game.abilityMaxRank && s.content.abilities[a.id]?.evolution)
    .map((a) => EVOLVE_PREFIX + a.id)
    .slice(0, s.content.game.talentChoices);
  const take = (from: string[]) => {
    const left = from.filter((id) => !out.includes(id));
    if (left.length > 0) out.push(left[Math.floor(nextRandom(s) * left.length)]!);
  };
  take(skillPool);
  while (out.length < s.content.game.talentChoices && pool.some((id) => !out.includes(id))) take(pool);
  return out;
}

/** Przelicza modyfikatory i statystyki po zmianie talentów. Wzrost maks. HP leczy o tę samą wartość. */
export function refreshModifiers(s: GameState): void {
  const cls = s.content.classes[s.classId]!;
  const before = s.player.stats.maxHealth;
  // Aktywne zdolności okresowe rasy dokładają swoje modyfikatory na czas działania.
  const periodic = s.content.races[s.raceId]?.periodic ?? [];
  const active = periodic.filter((_, i) => (s.periodic[i]?.left ?? 0) > 0).flatMap((p) => p.modifiers);
  const relics = s.relics.flatMap((r) => s.content.relics[r.relicId]?.modifiers ?? []);
  s.modifiers = [...s.loadout, ...collectModifiers(s.content, s.talents), ...relics, ...active];
  s.player.stats = computePlayerStats(cls.baseStats, s.modifiers);
  const delta = s.player.stats.maxHealth - before;
  if (delta > 0) s.player.hp += delta;
  s.player.hp = Math.min(s.player.hp, s.player.stats.maxHealth);
}

export function checkLevelUp(s: GameState): void {
  if (s.pendingChoices || s.over || s.xp < s.xpToNext) return;
  s.xp -= s.xpToNext;
  s.level += 1;
  s.xpToNext = xpForLevel(s.content, s.level);
  s.events.push({ type: 'levelUp', level: s.level });
  const choices = rollTalentChoices(s);
  if (choices.length > 0) {
    s.pendingChoices = choices;
  } else {
    // Wszystkie talenty wykupione — zamiast wyboru leczymy.
    s.player.hp = Math.min(s.player.stats.maxHealth, s.player.hp + s.player.stats.maxHealth * 0.25);
  }
}

export function chooseTalent(s: GameState, talentId: string): void {
  if (!s.pendingChoices?.includes(talentId)) {
    throw new Error(`Talent "${talentId}" nie jest teraz do wyboru`);
  }
  const upgraded = upgradeTarget(talentId);
  const evolving = evolutionTarget(talentId);
  if (evolving) {
    const ability = s.abilities.find((a) => a.id === evolving);
    const into = s.content.abilities[evolving]?.evolution;
    // Ewolucja zachowuje rangę (i jej premie), zmienia samą umiejętność.
    if (ability && into) {
      ability.id = into;
      ability.cooldownLeft = Math.min(ability.cooldownLeft, 0.3);
    }
  } else if (upgraded) {
    const ability = s.abilities.find((a) => a.id === upgraded);
    if (ability) ability.rank += 1;
  } else {
    const talent = s.content.talents[talentId]!;
    s.talents[talentId] = (s.talents[talentId] ?? 0) + 1;
    const granted = talent.grantsAbility;
    if (granted && !s.abilities.some((a) => a.id === granted)) {
      s.abilities.push({ id: granted, cooldownLeft: 0.3, rank: 1 });
    }
  }
  refreshModifiers(s);
  s.pendingChoices = null;
  // Duży zastrzyk XP może dać kilka poziomów pod rząd.
  checkLevelUp(s);
}
